import { IconButton } from '@components/core/icon-button.component';
import { Modal } from '@components/core/modal.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { SoftDeleteButton } from '@components/shared/soft-delete-button.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { Skeleton } from '@components/ui/skeleton';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useClipboardCopy } from '@hooks/use-clipboard-copy.hook';
import {
  useAdminAvailableParentCategoriesQuery,
  useAdminCategoryQuery,
  useAdminDeleteCategory,
  useAdminUpdateCategory,
  useAdminUpdateCategoryStatus,
} from '@queries/admin-categories.query';
import { categorySchema } from '@schemas/category.schema';
import { useStore } from '@tanstack/react-form';
import { safeFormatDateTime } from '@utils/date.util';
import { mapToSelectOptions } from '@utils/select.util';
import { CheckIcon, CopyIcon, EditIcon, XIcon } from 'lucide-react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { Suspense, useState } from 'react';
import { AdminCategoryDependencies } from './admin-category-dependencies.component';
import { AdminCategoryFormData, adminCategoryFormOptions } from './admin-category-form-options';
import { AdminCategorySubcategories } from './admin-category-subcategories.component';

const AdminCategoryModalHeaderTitle: React.FC<{ categoryId: string }> = (props) => {
  const { categoryId } = props;
  const { data: category } = useAdminCategoryQuery(categoryId);

  return <span>Category: {category?.name || ''}</span>;
};

type AdminCategoryModalProps = {
  categoryId: string;
  isOpen: boolean;
  onClose: () => void;
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-4">
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Details Column */}
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-16" />

            {/* Status */}
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-6 w-20" />
            </div>

            {/* Name Field */}
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-10 w-full" />
            </div>

            {/* Description Field */}
            <div className="flex flex-col gap-1">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </div>

        {/* Usage Column */}
        <div className="p-4 rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-16" />

            {/* Usage Items */}
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between"
              >
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-8" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subcategories Section */}
      <div className="p-4 rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-9 w-40" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminCategoryModal: React.FC<AdminCategoryModalProps> = (props) => {
  const { categoryId, isOpen, onClose } = props;

  const [, setSelectedCategoryId] = useQueryState('categoryId', parseAsString);
  const { mutateAsync: updateCategory, isPending: isUpdating } = useAdminUpdateCategory();
  const { mutateAsync: updateCategoryStatus, isPending: isUpdatingStatus } = useAdminUpdateCategoryStatus();
  const { mutateAsync: deleteCategory, isPending: isDeleting } = useAdminDeleteCategory();
  const { data: parentCategoriesData, isPending: isLoadingParents } =
    useAdminAvailableParentCategoriesQuery(categoryId);

  const { data: category, isPending: isPendingCategory } = useAdminCategoryQuery(categoryId);

  const { handleCopy, isCopied } = useClipboardCopy();

  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isEditingSubcategories, setIsEditingSubcategories] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isBusy = isUpdating || isUpdatingStatus || isDeleting;
  const totalSubcategoryDependencies =
    category?.subcategories?.reduce((sum, subcategory) => sum + (subcategory.totalDependencies ?? 0), 0) ?? 0;

  const totalDependenciesAll = (category?.totalDependencies ?? 0) + totalSubcategoryDependencies;
  const canDelete = totalDependenciesAll === 0;

  const form = useAppForm({
    ...adminCategoryFormOptions,
    defaultValues: {
      description: category?.description ?? undefined,
      name: category?.name ?? '',
      parentCategoryId: category?.parentCategory?.id ?? null,
      subcategories: category?.subcategories?.map((subcategory) => subcategory.id) ?? [],
    } as AdminCategoryFormData,

    validators: {
      onSubmit: categorySchema,
    },

    onSubmit: async ({ value, formApi }) => {
      await updateCategory(
        {
          dto: value,
          id: categoryId,
        },
        {
          onSuccess: () => {
            successToast({ description: 'Category updated successfully' });
            formApi.reset();
            if (isEditingDetails) {
              setIsEditingDetails(false);
            }
            if (isEditingSubcategories) {
              setIsEditingSubcategories(false);
            }
          },
          onError: (error) => {
            errorToast({ description: error.message || 'Failed to update category' });
          },
        },
      );
    },
  });

  const hasParentCategory = useStore(form.store, (state) => !!state.values.parentCategoryId);
  const hasSubcategories = useStore(form.store, (state) => !!state.values.subcategories?.length);

  // Determine display mode based on hierarchy
  // - hasSubcategories: this is a parent category → hide parent field, show subcategories tile
  // - hasParentCategory: this is a subcategory → show parent field, hide subcategories tile
  // - neither: standalone category → show parent field AND subcategories tile
  const isParentCategory = hasSubcategories;
  const isSubcategory = hasParentCategory;
  const isStandalone = !hasSubcategories && !hasParentCategory;

  const showParentField = isSubcategory || isStandalone;
  const showSubcategoriesTile = isParentCategory || isStandalone;

  const parentOptions = mapToSelectOptions(parentCategoriesData?.categories ?? []);

  const handleDelete = async () => {
    await deleteCategory(categoryId, {
      onSuccess: () => {
        successToast({ description: 'Category deleted successfully' });
        void setSelectedCategoryId(null);
        onClose();
      },
      onError: (error) => errorToast({ description: error.message || 'Failed to delete category' }),
    });
  };

  const handleUpdateCategoryStatus = async (archived: boolean) => {
    await updateCategoryStatus(
      { id: categoryId, archived },
      {
        onSuccess: () => successToast({ description: 'Category updated successfully' }),
        onError: (error) => errorToast({ description: error.message || 'Failed to update category' }),
      },
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      centered
      isOpen={isOpen}
      onClose={onClose}
      header={{
        className: 'text-left',
        title: (
          <Suspense fallback="Category">
            <AdminCategoryModalHeaderTitle categoryId={categoryId} />
          </Suspense>
        ),
      }}
      footer={{
        leftButton: (
          <SoftDeleteButton
            archived={category?.archived ?? false}
            canDelete={canDelete}
            disabled={isBusy}
            itemType="category"
            onArchive={() => handleUpdateCategoryStatus(true)}
            onDelete={() => setShowDeleteModal(true)}
            onUnarchive={() => handleUpdateCategoryStatus(false)}
          />
        ),
      }}
      width="840px"
    >
      {isPendingCategory ? (
        <LoadingSkeleton />
      ) : category ? (
        <>
          <div className="flex flex-col gap-4">
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-4">
                <Tile
                  className="border border-gray-200 shadow-none"
                  header={{
                    actions: isEditingDetails ? (
                      <div className="flex gap-1">
                        <IconButton
                          disabled={isBusy}
                          onClick={() => {
                            form.reset();
                            setIsEditingDetails(false);
                          }}
                          size="md"
                          icon={<XIcon size={16} />}
                          variant="secondary"
                        />

                        <IconButton
                          disabled={isBusy}
                          onClick={() => form.handleSubmit()}
                          size="md"
                          icon={<CheckIcon size={16} />}
                          variant="primary"
                        />
                      </div>
                    ) : undefined,
                    iconButton: isEditingDetails
                      ? undefined
                      : {
                          disabled: isEditingSubcategories,
                          disabledTooltip: 'Save or discard changes before editing',
                          icon: <EditIcon size={16} />,
                          onClick: () => setIsEditingDetails(true),
                        },
                    title: 'Details',
                  }}
                  hoverReveal={!isEditingDetails && !isEditingSubcategories}
                >
                  <div className="flex flex-col gap-3">
                    {/* ID */}
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-gray-500">ID</span>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900 font-mono text-xs truncate">{categoryId}</span>
                        <IconButton
                          ariaLabel="Copy ID"
                          className="border border-gray-200 shrink-0"
                          icon={isCopied('ID') ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
                          onClick={() => handleCopy('ID', categoryId)}
                          size="sm"
                          variant="secondary"
                        />
                      </div>
                    </div>

                    {isEditingDetails ? (
                      <form.AppForm>
                        <div className="flex flex-col gap-4">
                          {/* Parent Category (edit mode) */}
                          {showParentField && (
                            <form.AppField name="parentCategoryId">
                              {(field) => (
                                <field.SelectPopoverField
                                  disabled={isBusy || isLoadingParents}
                                  label="Parent Category"
                                  onClear={() => field.handleChange(null)}
                                  options={parentOptions}
                                  placeholder="Select a parent category"
                                />
                              )}
                            </form.AppField>
                          )}

                          {/* Name (edit mode) */}
                          <form.AppField name="name">
                            {(field) => (
                              <field.TextField
                                autoComplete="off"
                                data-1p-ignore
                                disabled={isBusy}
                                label="Name"
                                placeholder="Enter a category name"
                              />
                            )}
                          </form.AppField>

                          {/* Status (view only, shown during edit) */}
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-500">Status</span>
                            <div className="w-auto">
                              <Badge
                                className="w-auto"
                                size="sm"
                                variant={category.archived ? 'warning' : 'success'}
                              >
                                {category.archived ? 'Archived' : 'Available'}
                              </Badge>
                            </div>
                          </div>

                          {/* Description (edit mode) */}
                          <form.AppField name="description">
                            {(field) => (
                              <field.TextAreaField
                                disabled={isBusy}
                                label="Description"
                                placeholder="Enter a category description"
                                rows={3}
                              />
                            )}
                          </form.AppField>
                        </div>
                      </form.AppForm>
                    ) : (
                      <>
                        {/* Parent Category (view mode) */}
                        {category.parentCategory && (
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-gray-500">Parent Category</span>
                            <Button
                              className="sm:w-auto justify-start"
                              onClick={() => void setSelectedCategoryId(category.parentCategory!.id)}
                              variant="link"
                            >
                              {category.parentCategory.name}
                            </Button>
                          </div>
                        )}

                        {/* Name (view mode) */}
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Name</span>
                          <span className="text-sm text-gray-900">{category.name}</span>
                        </div>

                        {/* Status (view mode) */}
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Status</span>
                          <div className="w-auto">
                            <Badge
                              className="w-auto"
                              size="sm"
                              variant={category.archived ? 'warning' : 'success'}
                            >
                              {category.archived ? 'Archived' : 'Available'}
                            </Badge>
                          </div>
                        </div>

                        {/* Description (view mode) */}
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Description</span>
                          <span className="text-sm text-gray-900">{category.description ?? '–'}</span>
                        </div>

                        {/* Updated (view mode) */}
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Updated</span>
                          <span className="text-sm text-gray-900">{safeFormatDateTime(category.updatedAt)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </Tile>
              </div>

              <div className="flex flex-col gap-4">
                <AdminCategoryDependencies category={category} />
              </div>
            </section>

            {showSubcategoriesTile && (
              <AdminCategorySubcategories
                category={category}
                disableEdit={isEditingDetails}
                form={form}
                isBusy={isBusy}
                isEditing={isEditingSubcategories}
                onDiscard={() => {
                  form.reset();
                  setIsEditingSubcategories(false);
                }}
                onEdit={() => setIsEditingSubcategories(true)}
                onSave={() => {
                  void form.handleSubmit();
                }}
              />
            )}
          </div>
        </>
      ) : (
        <div className="text-sm text-gray-500">No category found.</div>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        itemName={category ? `category "${category.name}"` : 'category'}
        onConfirm={handleDelete}
        setIsOpen={setShowDeleteModal}
      />
    </Modal>
  );
};
